import { type LearnerProfileInput } from './api'

/**
 * FR-02 profile rules, mirrored from `app_private.validate_learner_fields`
 * so the form can explain a problem before submitting. The server remains
 * the authority and re-validates everything.
 */

export const NICKNAME_MAX_LENGTH = 30
export const DAILY_MINUTES_MIN = 10
export const DAILY_MINUTES_MAX = 40
export const DAILY_MINUTES_DEFAULT = 20
export const MAX_INTERESTS = 5
/** Accepted birth years: 3 to 20 years before the current year. */
export const MIN_AGE_YEARS = 3
export const MAX_AGE_YEARS = 20

/** ISO weekday numbers (1 = Monday … 7 = Sunday) with Vietnamese labels. */
export const STUDY_DAYS = [
  { day: 1, short: 'T2', long: 'Thứ Hai' },
  { day: 2, short: 'T3', long: 'Thứ Ba' },
  { day: 3, short: 'T4', long: 'Thứ Tư' },
  { day: 4, short: 'T5', long: 'Thứ Năm' },
  { day: 5, short: 'T6', long: 'Thứ Sáu' },
  { day: 6, short: 'T7', long: 'Thứ Bảy' },
  { day: 7, short: 'CN', long: 'Chủ Nhật' },
] as const

export function birthYearOptions(now: Date = new Date()): number[] {
  const year = now.getFullYear()
  const years: number[] = []
  for (let y = year - MIN_AGE_YEARS; y >= year - MAX_AGE_YEARS; y--) years.push(y)
  return years
}

export type ProfileField = keyof LearnerProfileInput

/** Vietnamese, parent-facing messages keyed by field. Empty when valid. */
export function validateProfile(
  profile: LearnerProfileInput,
  now: Date = new Date(),
): Partial<Record<ProfileField, string>> {
  const errors: Partial<Record<ProfileField, string>> = {}
  const nickname = profile.nickname.trim()
  if (nickname.length === 0) errors.nickname = 'Hãy nhập tên gọi của bé.'
  else if (nickname.length > NICKNAME_MAX_LENGTH) {
    errors.nickname = `Tên gọi tối đa ${String(NICKNAME_MAX_LENGTH)} ký tự.`
  }

  if (!birthYearOptions(now).includes(profile.birth_year)) {
    errors.birth_year = 'Hãy chọn năm sinh của bé.'
  }

  if (
    !Number.isInteger(profile.daily_minutes) ||
    profile.daily_minutes < DAILY_MINUTES_MIN ||
    profile.daily_minutes > DAILY_MINUTES_MAX
  ) {
    errors.daily_minutes = `Thời lượng từ ${String(DAILY_MINUTES_MIN)} đến ${String(DAILY_MINUTES_MAX)} phút.`
  }

  const days = new Set(profile.study_days)
  if (
    days.size === 0 ||
    days.size !== profile.study_days.length ||
    profile.study_days.some((d) => !Number.isInteger(d) || d < 1 || d > 7)
  ) {
    errors.study_days = 'Hãy chọn ít nhất một ngày học.'
  }

  if (
    profile.interests.length > MAX_INTERESTS ||
    new Set(profile.interests).size !== profile.interests.length
  ) {
    errors.interests = `Chọn tối đa ${String(MAX_INTERESTS)} sở thích.`
  }

  if (profile.exam_date !== null && !isIsoDate(profile.exam_date)) {
    errors.exam_date = 'Ngày thi không hợp lệ.'
  }
  return errors
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value)
}
